import React, { useState, useMemo } from 'react';
import { useCard } from '../../themes/cardStyles';
import { buildPaletteTheme, hpColor } from '../../themes/palettes';
import { CharacterAvatar } from '../primitives/CharacterAvatar';
import { ResourceBar } from '../primitives/ResourceBar';

export const ActiveCharacterCard = ({ theme, density, char, palette, cardStyle, cardFinish = 'solid', campaignName, onContinue, isDemo = false }) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const e = theme.effects || {};
  const [hovered, setHovered] = useState(false);
  const [continueHover, setContinueHover] = useState(false);
  const [tab, setTab] = useState('last');

  // Run through the shared palette-theme builder so the hero card picks up
  // the active campaign's palette in its surface, same way the campaign
  // cards below do. Uses campaign-profile alphas because this is hero
  // content, not secondary like solo cards.
  const tintedTheme = useMemo(
    () => buildPaletteTheme(theme, palette, cardStyle, cardFinish),
    [theme, palette, cardStyle, cardFinish]
  );
  const cardStyleObj = useCard(tintedTheme, cardStyle, hovered);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...cardStyleObj,
        padding: density.spacing.cardPadding,
        marginBottom: density.spacing.cardGap,
        position: 'relative',
        overflow: 'hidden',
        transition: e.transition,
      }}
    >
      {/* Palette-tinted top accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 2,
        background: `linear-gradient(90deg, transparent, ${palette.color}, transparent)`,
        opacity: 0.6,
      }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
        <div style={{
          width: 80, height: 80,
          borderRadius: '50%',
          background: c.bgInput,
          border: `2px solid ${palette.color}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          overflow: 'hidden',
          boxShadow: `0 0 28px ${palette.glow}, inset 0 0 12px rgba(0,0,0,0.5)`,
          flexShrink: 0,
        }}>
          <CharacterAvatar char={char} size={80} color={palette.light} fontFamily={t.headingFamily} />
        </div>

        <div style={{ flex: 1, minWidth: 260 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
            <span style={{
              fontFamily: t.headingFamily || 'Georgia, serif',
              fontSize: '1.75rem',
              fontWeight: 600,
              color: palette.light,
              textShadow: `${e.headingTextShadow || ''}, 0 0 18px ${palette.glow}`,
            }}>{char.name}</span>
            <span style={{ color: c.textSecondary, fontSize: '0.88rem', fontFamily: t.fontFamily }}>
              {char.race} {char.charClass} · L{char.level}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, maxWidth: 540 }}>
            <ResourceBar label="HP" current={char.hp} max={char.maxHp} color={hpColor(char.hp, char.maxHp, theme)} theme={theme} />
            <ResourceBar label="MP" current={char.mp} max={char.maxMp} color={c.mana || c.info} theme={theme} />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
          <div style={{
            display: 'inline-flex',
            background: 'rgba(0,0,0,0.4)',
            border: `1px solid ${c.borderLight}`,
            borderRadius: 999,
            padding: 3,
          }}>
            {['schedule', 'last'].map(tk => (
              <button key={tk} onClick={() => setTab(tk)} style={{
                padding: '5px 13px',
                background: tab === tk ? c.bgCardHover : 'transparent',
                border: 'none', borderRadius: 999,
                color: tab === tk ? c.accentLight : c.textMuted,
                fontSize: '0.72rem',
                cursor: 'pointer',
                fontFamily: t.fontFamily,
                textTransform: 'capitalize',
                display: 'flex', alignItems: 'center', gap: 5,
                transition: e.transition,
              }}>
                {tk === 'schedule' ? '📅' : '🕘'} {tk}
              </button>
            ))}
          </div>

          <button
            onMouseEnter={() => setContinueHover(true)}
            onMouseLeave={() => setContinueHover(false)}
            onClick={onContinue}
            style={{
              padding: '10px 26px',
              background: `linear-gradient(180deg, ${c.accent} 0%, ${c.accentDark} 100%)`,
              border: `1px solid ${c.accentLight}`,
              borderRadius: e.borderRadiusSm || '6px',
              color: c.textOnAccent,
              fontWeight: 700,
              fontSize: '0.94rem',
              cursor: 'pointer',
              fontFamily: t.fontFamily,
              boxShadow: continueHover
                ? `${e.glowAccent || ''}, 0 6px 18px ${c.accentGlow || c.accent + '55'}`
                : e.buttonShadow || '0 2px 8px rgba(0,0,0,0.4)',
              transform: continueHover ? 'translateY(-1px)' : 'none',
              transition: e.transition,
              display: 'flex', alignItems: 'center', gap: 8,
            }}
          >
            <span style={{ fontSize: '0.78rem' }}>▶</span> {isDemo ? 'Try demo' : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};
